import { useEffect, useRef, useState } from 'preact/hooks';
import type { MenuItem } from '../../shared/rules/menu';
import { useI18n, type MessageKey } from '../i18n';
import type { ShellUser } from '../lib/session';

interface Props {
  user: ShellUser;
  items: MenuItem[];
  path: string;
  onSignOut?: (() => void) | undefined;
}

// Disclosure pattern (button + panel of links), not an ARIA menu: Tab moves through it.
export function AccountMenu({ user, items, path, onSignOut }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const name = user.name ?? t('account.placeholderName');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  useEffect(() => setOpen(false), [path]);

  return (
    <div class="account" ref={root}>
      <button
        ref={button}
        type="button"
        class={`avatar avatar--${user.entity.toLowerCase()}`}
        aria-expanded={open}
        aria-controls="account-panel"
        aria-label={t('account.menu', { name })}
        onClick={() => setOpen(!open)}
      >
        <span aria-hidden="true">{user.initials}</span>
      </button>
      <div id="account-panel" class="account__panel" hidden={!open}>
        <p class="account__name">{name}</p>
        {items.length > 0 && (
          <nav aria-label={t('account.yourItems')}>
            <ul class="account__list">
              {items.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.path}`} aria-current={path === item.path ? 'page' : undefined}>
                    {t(`nav.${item.id}` as MessageKey)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
        {onSignOut && (
          <button type="button" class="account__signout" onClick={onSignOut}>
            {t('account.signOut')}
          </button>
        )}
      </div>
    </div>
  );
}
